/** Launch intents from pre-contract → configure. */

import type { SavedRoofModulesLayout } from '../../@types/types';

export type PreContractLaunchAction = 'runAi' | 'runPricing' | 'openDesign';

export const PRE_CONTRACT_OPEN_CANVAS_KEY = 'preContractOpenCanvas';
export const PRE_CONTRACT_LAUNCH_ACTION_KEY = 'preContractLaunchAction';
export const PRE_CONTRACT_OPEN_DESIGN_KEY = 'preContractOpenDesign';

/** Module/roof inclusion to apply in OneDraw after Open in OneDraw. */
export type PreContractOpenDesignPayload = {
    includedModuleIds: string[];
    includedRoofTagIds: number[];
    /** Exact 3D placement captured from the pre-contract embed for the selected option. */
    modulesLayout?: SavedRoofModulesLayout;
    /** Trees to keep ghosted (Tree Cutting layouts). */
    ghostedTreeIds?: string[];
};

function parseModulesLayout(raw: unknown): SavedRoofModulesLayout | undefined {
    if (!Array.isArray(raw) || raw.length === 0) return undefined;
    const layout = raw.filter((entry) => {
        if (!entry || typeof entry !== 'object') return false;
        const row = entry as { roofFaceId?: unknown; moduleArr?: unknown };
        return typeof row.roofFaceId === 'string' && Array.isArray(row.moduleArr) && row.moduleArr.length > 0;
    }) as SavedRoofModulesLayout;
    return layout.length > 0 ? layout : undefined;
}

function parseGhostedTreeIds(raw: unknown): string[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    return raw
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
        .map((id) => id.trim());
}

export function markPreContractLaunch(action: PreContractLaunchAction): void {
    try {
        sessionStorage.setItem(PRE_CONTRACT_LAUNCH_ACTION_KEY, action);
        if (action === 'runAi' || action === 'openDesign') {
            sessionStorage.setItem(PRE_CONTRACT_OPEN_CANVAS_KEY, '1');
        } else {
            sessionStorage.removeItem(PRE_CONTRACT_OPEN_CANVAS_KEY);
        }
    } catch {
        /* ignore */
    }
}

export function persistPreContractOpenDesign(payload: PreContractOpenDesignPayload): void {
    try {
        sessionStorage.setItem(PRE_CONTRACT_OPEN_DESIGN_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

export function peekPreContractOpenDesign(): PreContractOpenDesignPayload | null {
    try {
        const raw = sessionStorage.getItem(PRE_CONTRACT_OPEN_DESIGN_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<PreContractOpenDesignPayload>;
        const includedModuleIds = Array.isArray(parsed.includedModuleIds)
            ? parsed.includedModuleIds.filter((id) => typeof id === 'string' && id.trim())
            : [];
        const includedRoofTagIds = Array.isArray(parsed.includedRoofTagIds)
            ? parsed.includedRoofTagIds.filter((id) => typeof id === 'number' && Number.isFinite(id))
            : [];
        if (includedModuleIds.length === 0) return null;
        const modulesLayout = parseModulesLayout(parsed.modulesLayout);
        const ghostedTreeIds = parseGhostedTreeIds(parsed.ghostedTreeIds);
        return {
            includedModuleIds,
            includedRoofTagIds,
            ...(modulesLayout ? { modulesLayout } : {}),
            ...(ghostedTreeIds ? { ghostedTreeIds } : {}),
        };
    } catch {
        return null;
    }
}

export function consumePreContractOpenDesign(): PreContractOpenDesignPayload | null {
    const payload = peekPreContractOpenDesign();
    try {
        sessionStorage.removeItem(PRE_CONTRACT_OPEN_DESIGN_KEY);
    } catch {
        /* ignore */
    }
    return payload;
}

export function peekPreContractLaunchAction(): PreContractLaunchAction | null {
    try {
        const raw = sessionStorage.getItem(PRE_CONTRACT_LAUNCH_ACTION_KEY);
        if (raw === 'runAi' || raw === 'runPricing' || raw === 'openDesign') return raw;
        return null;
    } catch {
        return null;
    }
}

export function consumePreContractLaunchAction(): PreContractLaunchAction | null {
    const action = peekPreContractLaunchAction();
    try {
        sessionStorage.removeItem(PRE_CONTRACT_LAUNCH_ACTION_KEY);
    } catch {
        /* ignore */
    }
    return action;
}

/** True when configure should open the 3D viewport instead of the oneBUTTON dashboard. */
export function shouldOpenCanvasOnConfigureLoad(): boolean {
    try {
        if (new URLSearchParams(window.location.search).get('view') === 'onedraw') {
            return true;
        }
        if (sessionStorage.getItem(PRE_CONTRACT_OPEN_CANVAS_KEY) === '1') {
            return true;
        }
        return peekPreContractLaunchAction() === 'runAi' || peekPreContractLaunchAction() === 'openDesign';
    } catch {
        return false;
    }
}

export function clearPreContractOpenCanvasFlag(): void {
    try {
        sessionStorage.removeItem(PRE_CONTRACT_OPEN_CANVAS_KEY);
    } catch {
        /* ignore */
    }
}

export const PRE_CONTRACT_RETURN_KEY = 'preContractReturnToIntake';
export const PRE_CONTRACT_RETURN_STEP_KEY = 'preContractReturnBoardStep';
export const PRE_CONTRACT_AUTO_FINALIZE_KEY = 'preContractAutoFinalize';
/** Dispatched after a pre-contract Open Design apply finishes and Finalize should open. */
export const PCI_PRECONTRACT_READY_TO_FINALIZE = 'pci-precontract-ready-to-finalize';

export type PreContractBoardStep = 1 | 2 | 3 | 4;

/** Remember that OneDraw was opened from pre-contract so the viewer can offer a back button. */
export function markPreContractReturn(step: PreContractBoardStep): void {
    try {
        sessionStorage.setItem(PRE_CONTRACT_RETURN_KEY, '1');
        sessionStorage.setItem(PRE_CONTRACT_RETURN_STEP_KEY, String(step));
    } catch {
        /* ignore */
    }
}

export function hasPreContractReturn(): boolean {
    try {
        return sessionStorage.getItem(PRE_CONTRACT_RETURN_KEY) === '1';
    } catch {
        return false;
    }
}

export function peekPreContractReturnStep(): PreContractBoardStep | null {
    try {
        const raw = sessionStorage.getItem(PRE_CONTRACT_RETURN_STEP_KEY);
        if (raw === '1' || raw === '2' || raw === '3' || raw === '4') return Number(raw) as PreContractBoardStep;
        return null;
    } catch {
        return null;
    }
}

/** Hide the viewer back button after returning to intake. Keeps the board step for restore. */
export function clearPreContractReturnFlag(): void {
    try {
        sessionStorage.removeItem(PRE_CONTRACT_RETURN_KEY);
    } catch {
        /* ignore */
    }
}

/** Clear return flag and board step (fresh intake from the sites table). */
export function clearPreContractReturn(): void {
    try {
        sessionStorage.removeItem(PRE_CONTRACT_RETURN_KEY);
        sessionStorage.removeItem(PRE_CONTRACT_RETURN_STEP_KEY);
    } catch {
        /* ignore */
    }
}

/** After Open Design apply, Actionbar should open the Save Design / Finalize modal. */
export function markPreContractAutoFinalize(): void {
    try {
        sessionStorage.setItem(PRE_CONTRACT_AUTO_FINALIZE_KEY, '1');
    } catch {
        /* ignore */
    }
}

export function peekPreContractAutoFinalize(): boolean {
    try {
        return sessionStorage.getItem(PRE_CONTRACT_AUTO_FINALIZE_KEY) === '1';
    } catch {
        return false;
    }
}

export function consumePreContractAutoFinalize(): boolean {
    const pending = peekPreContractAutoFinalize();
    try {
        sessionStorage.removeItem(PRE_CONTRACT_AUTO_FINALIZE_KEY);
    } catch {
        /* ignore */
    }
    return pending;
}

export function clearPreContractAutoFinalize(): void {
    try {
        sessionStorage.removeItem(PRE_CONTRACT_AUTO_FINALIZE_KEY);
    } catch {
        /* ignore */
    }
}
